// ============================================================================
// IAM ENDPOINTS
// ============================================================================
// Endpoint definitions for IAM / RBAC module

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

// ============================================================================
// ROLES
// ============================================================================
export const roleEndpoints = {
  /** GET /api/v1/roles */
  list: () => buildEndpoint('/roles'),
  /** GET /api/v1/roles/:id */
  getById: (id: ID) => buildEndpointWithParams('/roles/:id', { id }),
  /** POST /api/v1/roles */
  create: () => buildEndpoint('/roles'),
  /** PUT /api/v1/roles/:id */
  update: (id: ID) => buildEndpointWithParams('/roles/:id', { id }),
  /** DELETE /api/v1/roles/:id */
  delete: (id: ID) => buildEndpointWithParams('/roles/:id', { id }),
  /** GET /api/v1/roles/validate/code/:code */
  validateCode: (code: string, excludeId?: ID) =>
    buildEndpointWithParams('/roles/validate/code/:code', { code }) +
    (excludeId ? `?excludeId=${excludeId}` : ''),
} as const;

// ============================================================================
// ROLE PERMISSIONS
// ============================================================================
export const rolePermissionEndpoints = {
  /** GET /api/v1/roles/:roleId/permissions */
  getTree: (roleId: ID) => buildEndpointWithParams('/roles/:roleId/permissions', { roleId }),
  /** PUT /api/v1/roles/:roleId/permissions */
  bulkAssign: (roleId: ID) => buildEndpointWithParams('/roles/:roleId/permissions', { roleId }),
} as const;

// ============================================================================
// USER ROLES
// ============================================================================
export const userRoleEndpoints = {
  /** GET /api/v1/users/:userId/roles */
  list: (userId: ID) => buildEndpointWithParams('/users/:userId/roles', { userId }),
  /** GET /api/v1/users/:userId/roles/:id */
  getById: (userId: ID, id: ID) => buildEndpointWithParams('/users/:userId/roles/:id', { userId, id }),
  /** POST /api/v1/users/:userId/roles */
  assign: (userId: ID) => buildEndpointWithParams('/users/:userId/roles', { userId }),
  /** PUT /api/v1/users/:userId/roles/:id */
  update: (userId: ID, id: ID) => buildEndpointWithParams('/users/:userId/roles/:id', { userId, id }),
  /** DELETE /api/v1/users/:userId/roles/:id */
  revoke: (userId: ID, id: ID) => buildEndpointWithParams('/users/:userId/roles/:id', { userId, id }),
} as const;

// ============================================================================
// USER SCOPES
// ============================================================================
export const userScopeEndpoints = {
  /** GET /api/v1/users/:userId/scopes */
  list: (userId: ID) => buildEndpointWithParams('/users/:userId/scopes', { userId }),
  /** GET /api/v1/users/:userId/scopes/:id */
  getById: (userId: ID, id: ID) => buildEndpointWithParams('/users/:userId/scopes/:id', { userId, id }),
  /** POST /api/v1/users/:userId/scopes */
  create: (userId: ID) => buildEndpointWithParams('/users/:userId/scopes', { userId }),
  /** DELETE /api/v1/users/:userId/scopes/:id */
  delete: (userId: ID, id: ID) => buildEndpointWithParams('/users/:userId/scopes/:id', { userId, id }),
} as const;

// ============================================================================
// MODULES
// ============================================================================
export const moduleEndpoints = {
  /** GET /api/v1/modules */
  list: () => buildEndpoint('/modules'),
  /** GET /api/v1/modules/:id */
  getById: (id: ID) => buildEndpointWithParams('/modules/:id', { id }),
  /** POST /api/v1/modules */
  create: () => buildEndpoint('/modules'),
  /** PUT /api/v1/modules/:id */
  update: (id: ID) => buildEndpointWithParams('/modules/:id', { id }),
  /** DELETE /api/v1/modules/:id */
  delete: (id: ID) => buildEndpointWithParams('/modules/:id', { id }),
  /** GET /api/v1/modules/validate/code/:code */
  validateCode: (code: string, excludeId?: ID) =>
    buildEndpointWithParams('/modules/validate/code/:code', { code }) +
    (excludeId ? `?excludeId=${excludeId}` : ''),

  // --- MENUS (nested under module) ---
  /** GET /api/v1/modules/:moduleId/menus */
  listMenus: (moduleId: ID) => buildEndpointWithParams('/modules/:moduleId/menus', { moduleId }),
  /** POST /api/v1/modules/:moduleId/menus */
  createMenu: (moduleId: ID) => buildEndpointWithParams('/modules/:moduleId/menus', { moduleId }),
  /** PUT /api/v1/modules/:moduleId/menus/:id */
  updateMenu: (moduleId: ID, id: ID) => buildEndpointWithParams('/modules/:moduleId/menus/:id', { moduleId, id }),
  /** DELETE /api/v1/modules/:moduleId/menus/:id */
  deleteMenu: (moduleId: ID, id: ID) => buildEndpointWithParams('/modules/:moduleId/menus/:id', { moduleId, id }),
} as const;

// ============================================================================
// MENU-SCREEN LINKS
// ============================================================================
export const menuScreenEndpoints = {
  /** GET /api/v1/menus/:menuId/screens */
  list: (menuId: ID) => buildEndpointWithParams('/menus/:menuId/screens', { menuId }),
  /** POST /api/v1/menus/:menuId/screens */
  link: (menuId: ID) => buildEndpointWithParams('/menus/:menuId/screens', { menuId }),
  /** DELETE /api/v1/menus/:menuId/screens/:screenId */
  unlink: (menuId: ID, screenId: ID) => buildEndpointWithParams('/menus/:menuId/screens/:screenId', { menuId, screenId }),
} as const;

// ============================================================================
// SCREENS
// ============================================================================
export const screenEndpoints = {
  /** GET /api/v1/screens */
  list: () => buildEndpoint('/screens'),
  /** GET /api/v1/screens/:id */
  getById: (id: ID) => buildEndpointWithParams('/screens/:id', { id }),
  /** POST /api/v1/screens */
  create: () => buildEndpoint('/screens'),
  /** PUT /api/v1/screens/:id */
  update: (id: ID) => buildEndpointWithParams('/screens/:id', { id }),
  /** DELETE /api/v1/screens/:id */
  delete: (id: ID) => buildEndpointWithParams('/screens/:id', { id }),

  // --- ELEMENTS (nested under screen) ---
  /** GET /api/v1/screens/:screenId/elements */
  listElements: (screenId: ID) => buildEndpointWithParams('/screens/:screenId/elements', { screenId }),
  /** POST /api/v1/screens/:screenId/elements */
  createElement: (screenId: ID) => buildEndpointWithParams('/screens/:screenId/elements', { screenId }),
  /** PUT /api/v1/screens/:screenId/elements/:id */
  updateElement: (screenId: ID, id: ID) => buildEndpointWithParams('/screens/:screenId/elements/:id', { screenId, id }),
  /** DELETE /api/v1/screens/:screenId/elements/:id */
  deleteElement: (screenId: ID, id: ID) => buildEndpointWithParams('/screens/:screenId/elements/:id', { screenId, id }),
} as const;

// ============================================================================
// USER PERMISSION OVERRIDES
// ============================================================================
export const userPermissionEndpoints = {
  /** GET /api/v1/users/:userId/permissions/tree — user-level override tree */
  getTree: (userId: ID) => buildEndpointWithParams('/users/:userId/permissions/tree', { userId }),
  /** PUT /api/v1/users/:userId/permissions — bulk-assign user-level overrides */
  bulkAssign: (userId: ID) => buildEndpointWithParams('/users/:userId/permissions', { userId }),
  /** GET /api/v1/users/:userId/permissions/effective — merged effective permissions */
  getEffective: (userId: ID) => buildEndpointWithParams('/users/:userId/permissions/effective', { userId }),
} as const;

// ============================================================================
// MY PERMISSIONS (current user)
// ============================================================================
export const myPermissionsEndpoints = {
  /** GET /api/v1/me/permissions */
  get: () => buildEndpoint('/me/permissions'),
  /** GET /api/v1/me/navigation */
  navigation: () => buildEndpoint('/me/navigation'),
} as const;

// ============================================================================
// USERS (Admin)
// ============================================================================
export const userAdminEndpoints = {
  /** GET /api/v1/users */
  list: () => buildEndpoint('/users'),
  /** GET /api/v1/users/:id */
  getById: (id: ID) => buildEndpointWithParams('/users/:id', { id }),
  /** POST /api/v1/users */
  create: () => buildEndpoint('/users'),
  /** PUT /api/v1/users/:id */
  update: (id: ID) => buildEndpointWithParams('/users/:id', { id }),
  /** DELETE /api/v1/users/:id */
  delete: (id: ID) => buildEndpointWithParams('/users/:id', { id }),
  /** GET /api/v1/users/validate/username/:userName */
  validateUserName: (userName: string, excludeId?: ID) =>
    buildEndpointWithParams('/users/validate/username/:userName', { userName }) +
    (excludeId ? `?excludeId=${excludeId}` : ''),
  /** GET /api/v1/users/lookup/:userName */
  lookup: (userName: string) =>
    buildEndpointWithParams('/users/lookup/:userName', { userName }),
} as const;
